import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { Observable, of, forkJoin, merge } from 'rxjs';
import { startWith, map, catchError } from 'rxjs/operators';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import {
    MatPaginatorModule,
    MatPaginator,
    PageEvent,
} from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';

import { BookingsService } from './services/bookings.service';
import { BookingsStateService } from './services/bookings-state.service';
import { BookingsNavigationService } from './services/bookings-navigation.service';
import {
    BookingFilterDto,
    BookingItemDto,
    TemporadaDto,
} from '@opb-workspace/shared';
import {
    formatDateForApi,
    parseDateFromApi,
    getDefaultDateRange,
} from '../../core/utils/date.utils';
import { AuthService } from '../../services/auth.service';
import { LiquidacionesService } from '../../services/liquidaciones.service';
import { UserService } from '../../services/user.service';
import * as ExcelJS from 'exceljs';

@Component({
    selector: 'app-bookings',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        MatCardModule,
        MatButtonModule,
        MatPaginatorModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatInputModule,
        MatIconModule,
        MatTooltipModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatProgressSpinnerModule,
        MatTableModule,
        MatSelectModule,
    ],
    templateUrl: './bookings.component.html',
    styleUrls: ['./bookings.component.scss'],
})
export class BookingsComponent implements OnInit {
    @ViewChild(MatPaginator) paginator!: MatPaginator;

    dataSource = new MatTableDataSource<BookingItemDto>([]);
    displayedColumns: string[] = [];

    isLoading = false;
    isGeneratingExcel = false;
    hasError = false;
    errorMessage = '';
    isClientRole = false;

    totalRegistros = 0;
    paginaActual = 1;
    elementosPorPagina = 10;

    // Filters
    filterClient = new FormControl<any>('');
    filterExporter = new FormControl<any>('');
    filterVessel = new FormControl<any>('');
    filterCarrier = new FormControl<any>('');
    filterBookingNumber = new FormControl('');
    filterBlNumber = new FormControl('');
    filterPol = new FormControl<any>('');
    filterPod = new FormControl<any>('');
    filterStatus = new FormControl<string | string[] | null>('');
    filterDateFrom = new FormControl<Date | null>(null);
    filterDateTo = new FormControl<Date | null>(null);
    filterSeason = new FormControl('');

    // Options
    clientsOptions: any[] = [];
    exportersOptions: any[] = [];
    vesselsOptions: any[] = [];
    carriersOptions: any[] = [];
    polOptions: any[] = [];
    podOptions: any[] = [];
    statusesOptions: any[] = [];
    seasonsOptions: any[] = [];

    // Filtered Options Observables
    filteredClients!: Observable<any[]>;
    filteredExporters!: Observable<any[]>;
    filteredVessels!: Observable<any[]>;
    filteredCarriers!: Observable<any[]>;
    filteredPol!: Observable<any[]>;
    filteredPod!: Observable<any[]>;

    constructor(
        private bookingsService: BookingsService,
        private stateService: BookingsStateService,
        private navigationService: BookingsNavigationService,
        private authService: AuthService,
        private liquidacionesService: LiquidacionesService,
        private userService: UserService,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.isClientRole = this.authService.hasRole('client');
        this.configurarColumnas();
        // Default Date Range: -2 weeks, +3 weeks
        const { from, to } = getDefaultDateRange();
        this.filterDateFrom.setValue(from);
        this.filterDateTo.setValue(to);
        this.setupDateLockBehavior();
        this.loadCombos();

        // Load state from URL or Storage
        const stateLoadedFromUrl = this.navigationService.loadStateFromUrl(
            this.route
        );

        if (!stateLoadedFromUrl) {
            this.stateService.clearState();
            // Wait for combos to load season before searching?
            // Or just search with dates.
            // We will search after combos load to include season if applicable.
            return;
        }

        this.restoreState();
        this.searchBookings();
    }

    loadCombos() {
        const clientesActivos$ = this.isClientRole
            ? of([] as { id: number; name: string }[])
            : this.userService.getActiveClients().pipe(
                  map((res) =>
                      res.success && res.data
                          ? res.data.map((c) => ({
                                id: c.clienteId,
                                name: (c.cliente || '').trim(),
                            }))
                          : []
                  ),
                  catchError(() => of([] as { id: number; name: string }[]))
              );

        forkJoin({
            filters: this.bookingsService.getFilters(),
            temporadas: this.liquidacionesService
                .getTemporadas()
                .pipe(catchError(() => of([] as TemporadaDto[]))),
            clientesActivos: clientesActivos$,
        }).subscribe(({ filters: data, temporadas, clientesActivos }) => {
            // Misma fuente que Liquidaciones Agencia: GET /api/v1/clients/active
            this.clientsOptions = clientesActivos;
            this.exportersOptions = data.exporters || [];
            this.vesselsOptions = data.vessels || [];
            this.carriersOptions = data.carriers || [];
            this.polOptions = data.polPorts || [];
            this.podOptions = data.podPorts || [];
            this.statusesOptions = data.statuses || [];
            this.seasonsOptions = this.mapTemporadasToSeasonOptions(temporadas);
            this.setupFilters();
            this.configurarFiltroCliente();
            this.selectDefaultSeason();
        });
    }

    /**
     * Convierte TemporadaDto (Commons) al formato usado por el combo de temporada.
     * Misma fuente que el módulo Liquidaciones VD: GET /api/v1/commons/temporadas
     */
    private mapTemporadasToSeasonOptions(temporadas: TemporadaDto[]): {
        seasonId: number;
        seasonName: string;
        seasonFrom: string;
        seasonTo: string;
    }[] {
        if (!Array.isArray(temporadas) || temporadas.length === 0) return [];
        return temporadas
            .slice()
            .sort((a, b) => {
                const dateB = new Date(
                    b.fechaInicio ?? b.fechaFin ?? ''
                ).getTime();
                const dateA = new Date(
                    a.fechaInicio ?? a.fechaFin ?? ''
                ).getTime();
                return dateB - dateA;
            })
            .map((t) => ({
                seasonId: t.id,
                seasonName: t.nombre?.trim() || this.formatearTemporadaLabel(t),
                seasonFrom: this.toYYYYMMDD(t.fechaInicio),
                seasonTo: this.toYYYYMMDD(t.fechaFin),
            }));
    }

    private formatearTemporadaLabel(t: TemporadaDto): string {
        const ini = new Date(t.fechaInicio).getFullYear();
        const fin = new Date(t.fechaFin).getFullYear();
        return ini === fin ? `${ini}` : `${ini}-${fin}`;
    }

    private toYYYYMMDD(value: string | undefined): string {
        if (!value) return '';
        const d = new Date(value);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    /** Configura columnas de la tabla según el rol del usuario */
    configurarColumnas(): void {
        const base = [
            'bookingNumber',
            'status',
            'exporterName',
            'carrierName',
            'vesselName',
            'bookingDate',
            'polName',
            'podName',
            'externalBookingNumber',
            'commodity',
            'containerCount',
            'actions',
        ];
        this.displayedColumns = this.isClientRole
            ? base
            : ['bookingNumber', 'clientName', ...base.slice(1)];
    }

    /** Pre-selecciona y deshabilita el combo cliente si el usuario tiene rol client */
    configurarFiltroCliente(): void {
        if (!this.isClientRole) return;
        const user = this.authService.getCurrentUser();
        if (!user?.clientId) return;
        const clientObj = this.clientsOptions.find(
            (c: any) => +c.id === +user.clientId!
        );
        if (clientObj) {
            this.filterClient.setValue(clientObj);
        } else {
            const fallback = {
                id: user.clientId,
                name: user.clientName?.trim() || `Cliente #${user.clientId}`,
            };
            this.filterClient.setValue(fallback);
        }
        this.filterClient.disable();
    }

    selectDefaultSeason() {
        // Logic: find season where current date is between seasonFrom and seasonTo
        if (!this.seasonsOptions || this.seasonsOptions.length === 0) return;

        // Only set default if no value is already set (e.g. from restoreState)
        if (this.filterSeason.value) return;

        const now = new Date().toISOString().split('T')[0]; // simple YYYY-MM-DD comparison
        // Assuming backend returns seasonFrom/To. If not, we might need to adjust or pick latest.
        // The query 'getActiveSeasonsQuery' returns seasonFrom/seasonTo as strings.

        // Check if seasonFrom/To exists in the options. Properties might be lowerCamelCase from the query?
        // Query returns: seasonId, seasonName, seasonFrom, seasonTo.

        const currentSeason = this.seasonsOptions.find((s) => {
            if (!s.seasonFrom || !s.seasonTo) return false;
            return now >= s.seasonFrom && now <= s.seasonTo;
        });

        if (currentSeason) {
            this.filterSeason.setValue(currentSeason.seasonId);
        } else {
            // Fallback to latest season if no match
            // Assuming sorted by DESC per query
            this.filterSeason.setValue(this.seasonsOptions[0].seasonId);
        }

        // Trigger search after setting default season if generic search wasn't triggered
        if (this.dataSource.data.length === 0 && !this.isLoading) {
            this.searchBookings();
        }
    }

    get datesLockedBySearch(): boolean {
        return this.filterDateFrom.disabled;
    }

    private setupDateLockBehavior(): void {
        merge(
            this.filterBookingNumber.valueChanges,
            this.filterBlNumber.valueChanges,
        ).subscribe(() => {
            const hasValue =
                !!this.filterBookingNumber.value?.trim() ||
                !!this.filterBlNumber.value?.trim();

            if (hasValue) {
                this.filterDateFrom.setValue(null, { emitEvent: false });
                this.filterDateTo.setValue(null, { emitEvent: false });
                this.filterDateFrom.disable({ emitEvent: false });
                this.filterDateTo.disable({ emitEvent: false });
                this.filterSeason.setValue('', { emitEvent: false });
                this.filterSeason.disable({ emitEvent: false });
            } else {
                this.filterDateFrom.enable({ emitEvent: false });
                this.filterDateTo.enable({ emitEvent: false });
                this.filterSeason.enable({ emitEvent: false });
            }
        });
    }

    setupFilters() {
        this.filteredClients = this.filterClient.valueChanges.pipe(
            startWith(''),
            map((value: any) => {
                const name = typeof value === 'string' ? value : value?.name; // Handle object if selected
                return name
                    ? this._filter(name as string, this.clientsOptions)
                    : this.clientsOptions.slice();
            })
        );

        this.filteredExporters = this.filterExporter.valueChanges.pipe(
            startWith(''),
            map((value: any) => {
                const name = typeof value === 'string' ? value : value?.name;
                return name
                    ? this._filter(name as string, this.exportersOptions)
                    : this.exportersOptions.slice();
            })
        );

        this.filteredVessels = this.filterVessel.valueChanges.pipe(
            startWith(''),
            map((value: any) => {
                const name = typeof value === 'string' ? value : value?.name;
                return name
                    ? this._filter(name as string, this.vesselsOptions)
                    : this.vesselsOptions.slice();
            })
        );

        this.filteredCarriers = this.filterCarrier.valueChanges.pipe(
            startWith(''),
            map((value: any) => {
                const name = typeof value === 'string' ? value : value?.name;
                return name
                    ? this._filter(name as string, this.carriersOptions)
                    : this.carriersOptions.slice();
            })
        );

        this.filteredPol = this.filterPol.valueChanges.pipe(
            startWith(''),
            map((value: any) => {
                const name = typeof value === 'string' ? value : value?.name;
                return name
                    ? this._filter(name as string, this.polOptions)
                    : this.polOptions.slice();
            })
        );

        this.filteredPod = this.filterPod.valueChanges.pipe(
            startWith(''),
            map((value: any) => {
                const name = typeof value === 'string' ? value : value?.name;
                return name
                    ? this._filter(name as string, this.podOptions)
                    : this.podOptions.slice();
            })
        );
    }

    private _filter(name: string, options: any[]): any[] {
        const filterValue = name.toLowerCase();
        return options.filter((option) =>
            option.name.toLowerCase().includes(filterValue)
        );
    }

    // Helper to display name in input
    displayFn(option: any): string {
        return option && option.name ? option.name.trim() : '';
    }

    /** Extrae el ID numérico de un valor de autocomplete (objeto o string) */
    private extractId(value: any): number | undefined {
        if (!value) return undefined;
        if (typeof value === 'object' && value.id != null) return +value.id;
        const num = +value;
        return isNaN(num) ? undefined : num;
    }

    /** Busca el objeto completo en las opciones del combo por ID */
    private findOption(options: any[], id: number | undefined): any {
        if (!id) return null;
        return options.find((o: any) => +o.id === id) || null;
    }

    restoreState() {
        const state = this.stateService.getCurrentState();
        const f = state.filters;
        const clientObj = this.findOption(this.clientsOptions, f.clientId);
        if (clientObj) this.filterClient.setValue(clientObj);
        const exporterObj = this.findOption(
            this.exportersOptions,
            f.exporterId
        );
        if (exporterObj) this.filterExporter.setValue(exporterObj);
        const vesselObj = this.findOption(this.vesselsOptions, f.vesselId);
        if (vesselObj) this.filterVessel.setValue(vesselObj);
        const carrierObj = this.findOption(this.carriersOptions, f.carrierId);
        if (carrierObj) this.filterCarrier.setValue(carrierObj);
        if (f.bookingNumber) this.filterBookingNumber.setValue(f.bookingNumber);
        if (f.blNumber) this.filterBlNumber.setValue(f.blNumber);
        const polObj = this.findOption(this.polOptions, f.polId);
        if (polObj) this.filterPol.setValue(polObj);
        const podObj = this.findOption(this.podOptions, f.podId);
        if (podObj) this.filterPod.setValue(podObj);
        if (f.status) this.filterStatus.setValue(f.status);
        if (f.season) this.filterSeason.setValue(f.season);
        if (f.dateFrom)
            this.filterDateFrom.setValue(parseDateFromApi(f.dateFrom));
        if (f.dateTo) this.filterDateTo.setValue(parseDateFromApi(f.dateTo));
        this.paginaActual = f.page || 1;
        this.elementosPorPagina = f.limit || 10;
    }

    buildFilters(): BookingFilterDto {
        const filters: BookingFilterDto = {};
        const clientId = this.extractId(this.filterClient.value);
        if (clientId) filters.clientId = clientId;
        const exporterId = this.extractId(this.filterExporter.value);
        if (exporterId) filters.exporterId = exporterId;
        const vesselId = this.extractId(this.filterVessel.value);
        if (vesselId) filters.vesselId = vesselId;
        const carrierId = this.extractId(this.filterCarrier.value);
        if (carrierId) filters.carrierId = carrierId;
        if (this.filterBookingNumber.value)
            filters.bookingNumber = this.filterBookingNumber.value;
        if (this.filterBlNumber.value)
            filters.blNumber = this.filterBlNumber.value;
        const polId = this.extractId(this.filterPol.value);
        if (polId) filters.polId = polId;
        const podId = this.extractId(this.filterPod.value);
        if (podId) filters.podId = podId;
        if (this.filterStatus.value) {
            filters.status = this.filterStatus.value;
        }
        if (this.filterSeason.value && !this.filterSeason.disabled) filters.season = this.filterSeason.value;
        if (this.filterDateFrom.value && !this.filterDateFrom.disabled)
            filters.dateFrom = formatDateForApi(this.filterDateFrom.value);
        if (this.filterDateTo.value && !this.filterDateTo.disabled)
            filters.dateTo = formatDateForApi(this.filterDateTo.value);
        return filters;
    }

    applyFilters(): void {
        this.paginaActual = 1;
        const filters = this.buildFilters();

        this.stateService.updateFilters({
            season: undefined,
            dateFrom: undefined,
            dateTo: undefined,
            ...filters,
            page: 1,
            limit: this.elementosPorPagina,
        });

        this.navigationService.applyFilters();
        this.searchBookings();
    }

    searchBookings(): void {
        this.isLoading = true;
        this.hasError = false;

        const filtersBase = this.buildFilters();
        const filtersDto: BookingFilterDto = {
            ...filtersBase,
            page: this.paginaActual,
            limit: this.elementosPorPagina,
        };

        this.bookingsService.search(filtersDto).subscribe({
            next: (response) => {
                this.dataSource.data = response.data || [];
                this.totalRegistros = response.total || 0;
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error loading bookings', err);
                this.errorMessage = 'Error al cargar reservas.';
                this.hasError = true;
                this.isLoading = false;
            },
        });
    }

    clearFilters(): void {
        if (!this.isClientRole) {
            this.filterClient.setValue('');
        }
        this.filterExporter.setValue('');
        this.filterVessel.setValue('');
        this.filterCarrier.setValue('');
        this.filterBookingNumber.setValue('');
        this.filterBlNumber.setValue('');
        this.filterPol.setValue('');
        this.filterPod.setValue('');
        this.filterStatus.setValue('');
        this.filterSeason.setValue('');
        this.filterDateFrom.setValue(null);
        this.filterDateTo.setValue(null);
        this.navigationService.clearFilters();
        this.paginaActual = 1;
        this.searchBookings();
    }

    onPageChange(event: PageEvent): void {
        this.paginaActual = event.pageIndex + 1;
        this.elementosPorPagina = event.pageSize;

        this.stateService.updateFilters({
            page: this.paginaActual,
            limit: this.elementosPorPagina,
        });

        this.navigationService.applyFilters();
        this.searchBookings();
    }

    viewDetail(id: number): void {
        this.navigationService.openDetailInNewTab(id);
    }

    /**
     * Export all filtered data to Excel with professional styling
     */
    async exportExcel(): Promise<void> {
        if (this.totalRegistros === 0) {
            return;
        }

        this.isGeneratingExcel = true;

        try {
            // Build filters - export endpoint handles all data without pagination
            const filters = this.buildFilters();

            // Use export-specific endpoint that returns one row per container
            this.bookingsService.getExportData(filters).subscribe({
                next: async (data) => {
                    try {
                        await this.generateStyledExcel(data);
                        this.isGeneratingExcel = false;
                    } catch (error) {
                        console.error('Error generando Excel:', error);
                        this.isGeneratingExcel = false;
                    }
                },
                error: (err) => {
                    console.error('Error obteniendo datos:', err);
                    this.isGeneratingExcel = false;
                },
            });
        } catch (error) {
            console.error('Error exportando Excel:', error);
            this.isGeneratingExcel = false;
        }
    }

    /**
     * Generate Excel file with professional styling matching Liquidaciones Agencia format
     */
    private async generateStyledExcel(data: any[]): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Reservas');

        // OPB Brand Colors
        const colors = {
            primary: '1e3a8a', // Azul primario
            secondary: '3b82f6', // Azul secundario
            dark: '1e293b', // Azul oscuro
            gray: '64748b', // Gris
            light: 'f1f5f9', // Gris claro
            white: 'ffffff', // Blanco
        };

        // Try to load logo
        let logoArrayBuffer: ArrayBuffer | null = null;
        try {
            const baseHref =
                document.querySelector('base')?.getAttribute('href') || '/';
            const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
            const logoResponse = await fetch(logoPath);
            if (logoResponse.ok) {
                logoArrayBuffer = await logoResponse.arrayBuffer();
            }
        } catch (error) {
            console.warn('No se pudo cargar el logo');
        }

        // Add logo if available
        if (logoArrayBuffer) {
            try {
                const logoId = workbook.addImage({
                    buffer: logoArrayBuffer,
                    extension: 'png',
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0.2, row: 0.2 },
                    ext: { width: 150, height: 50 },
                });
            } catch (error) {
                console.warn('Error al agregar logo');
            }
        }

        // Title row
        const titleRow = worksheet.getRow(1);
        titleRow.height = 40;
        const titleCell = worksheet.getCell('D1');
        titleCell.value = 'REPORTE DE RESERVAS';
        titleCell.font = {
            name: 'Calibri',
            size: 20,
            bold: true,
            color: { argb: 'FF' + colors.primary },
        };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D1:H1');

        // Date row
        const dateRow = worksheet.getRow(2);
        dateRow.height = 25;
        const dateCell = worksheet.getCell('D2');
        dateCell.value = `Generado el: ${new Date().toLocaleDateString(
            'es-CL',
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }
        )}`;
        dateCell.font = {
            name: 'Calibri',
            size: 11,
            color: { argb: 'FF' + colors.gray },
        };
        dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D2:H2');

        // Empty row
        worksheet.getRow(3).height = 10;

        // Column definitions
        const columnDefs = [
            { header: 'Cliente', key: 'clientName', width: 30 },
            { header: 'Exportador', key: 'exporterName', width: 30 },
            { header: 'Nro. reserva', key: 'externalBookingNumber', width: 20 },
            { header: 'Nro. B/L', key: 'blNumber', width: 20 },
            { header: 'Nro. contenedor', key: 'containerNumber', width: 20 },
            { header: 'Tipo contenedor', key: 'containerType', width: 15 },
            { header: 'Estado', key: 'status', width: 15 },
            { header: 'S / Conv', key: 'shipConvenience', width: 10 },
            { header: 'Cant. containers', key: 'totalContainers', width: 10 },
            { header: 'Consignatario', key: 'consignee', width: 25 },
            { header: 'Emisión B/L', key: 'blType', width: 15 },
            { header: 'Término pago', key: 'paymentTerm', width: 15 },
            { header: 'Contrato naviera', key: 'carrierContract', width: 20 },
            { header: 'Nave', key: 'vesselName', width: 20 },
            { header: 'Nro. viaje', key: 'voyageNumber', width: 12 },
            { header: 'Naviera', key: 'carrierName', width: 20 },
            { header: 'POL', key: 'polName', width: 15 },
            { header: 'POD', key: 'podName', width: 15 },
            { header: 'Corte documental', key: 'documentalCutOff', width: 15 },
            { header: 'ETD', key: 'etd', width: 12 },
            { header: 'ETA', key: 'eta', width: 12 },
            { header: 'Stacking', key: 'stacking', width: 20 },
            { header: 'Late arrival', key: 'lateArrival', width: 20 },
            { header: 'Temperatura', key: 'temperature', width: 12 },
            { header: 'Un. Temp', key: 'temperatureUnit', width: 10 },
            { header: 'Ventilación', key: 'ventilation', width: 12 },
            { header: 'Un. vent.', key: 'ventilationUnit', width: 10 },
            { header: '% Humedad', key: 'humidity', width: 10 },
            { header: 'Tratamiento frío', key: 'coldTreatment', width: 15 },
            { header: 'Atmósfera', key: 'atmosphere', width: 15 },
            { header: 'O2', key: 'o2', width: 8 },
            { header: 'CO2', key: 'co2', width: 8 },
            { header: 'Especie', key: 'commodity', width: 20 },
            { header: 'Observaciones', key: 'remarks', width: 30 },
        ];

        columnDefs.forEach((col, index) => {
            worksheet.getColumn(index + 1).width = col.width;
        });

        const headerRow = worksheet.getRow(4);
        headerRow.height = 30;
        columnDefs.forEach((col, index) => {
            const cell = worksheet.getCell(4, index + 1);
            cell.value = col.header;
            cell.font = {
                name: 'Calibri',
                size: 12,
                bold: true,
                color: { argb: 'FFFFFFFF' },
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF' + colors.primary },
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true,
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF' + colors.dark } },
                bottom: {
                    style: 'medium',
                    color: { argb: 'FF' + colors.dark },
                },
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });

        data.forEach((item, index) => {
            const row = worksheet.getRow(5 + index);
            row.height = 22;
            const rowData = columnDefs.map((col) => {
                // Special mapping for container count in export (since it's one row per container)
                if (col.key === 'totalContainers') return 1;

                let val = item[col.key];
                // Special formatting
                if (
                    col.key === 'etd' ||
                    col.key === 'eta' ||
                    col.key === 'documentalCutOff'
                ) {
                    return this.formatDate(val);
                }
                return val === null || val === undefined ? '-' : val;
            });

            rowData.forEach((value, colIndex) => {
                const cell = row.getCell(colIndex + 1);
                cell.value = value;

                const fillColor =
                    index % 2 === 0
                        ? { argb: 'FFFFFFFF' }
                        : { argb: 'FF' + colors.light };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: fillColor,
                };
                cell.font = {
                    name: 'Calibri',
                    size: 10,
                    color: { argb: 'FF000000' },
                };

                if (typeof value === 'number') {
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                    };
                } else {
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'left',
                        wrapText: true,
                    };
                }

                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    bottom: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    left: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    right: { style: 'thin', color: { argb: 'FFd1d5db' } },
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reservas_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    // Helpers
    formatDate(dateStr: string): string {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('es-CL');
    }
}
